import Layout from "../components/Layout";
import LeadForm from "../components/LeadForm";
import { Mail, MapPin, Phone } from "lucide-react";
import { motion } from "motion/react";

const serviceAreas = [
  "Springfield",
  "Eugene",
  "Thurston",
  "Glenwood",
  "Fairmont",
];

const fadeUp = (delay = 0) => ({
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.5, delay },
});

export default function ContactPage() {

  return (
    <Layout>
      {/* Page head meta */}
      <title>
        Contact Superhero Jiu Jitsu Academy | Jiu Jitsu, MMA, Muay Thai
      </title>

      {/* Hero banner */}
      <section className="bg-hero py-14 px-4 text-center">
        <div className="container mx-auto max-w-2xl">
          <motion.span
            {...fadeUp(0)}
            className="inline-block text-xs font-display font-semibold tracking-widest uppercase text-gold mb-3"
          >
            Get In Touch
          </motion.span>
          <motion.h1
            {...fadeUp(0.1)}
            className="font-display font-black text-3xl sm:text-4xl md:text-5xl text-hero-foreground leading-tight text-balance"
          >
            Contact Superhero
            <br className="hidden sm:block" /> Jiu Jitsu Academy
          </motion.h1>
          <motion.p
            {...fadeUp(0.2)}
            className="mt-4 text-hero-foreground font-body text-base max-w-lg mx-auto leading-relaxed"
          >
            Get in touch with us. We're passionate about helping you get started
            with martial arts. Call or email us with any questions.
          </motion.p>
        </div>
      </section>

      {/* Gold divider */}
      <div className="h-1 bg-gradient-accent" />

      {/* Main content: two-column */}
      <section className="bg-background py-14 px-4">
        <div className="container mx-auto max-w-5xl">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-stretch">
            {/* LEFT — Lead capture form */}
            <motion.div
              {...fadeUp(0)}
              className="bg-card rounded-2xl shadow-elevated border border-border p-8 bg-primary/80 backdrop-blur-sm border border-gold/30"
            >
              <h2 className="font-display font-bold text-xl sm:text-2xl text-hero-foreground mb-2">
                Ready to get started?
              </h2>
              <p className="text-hero-foreground font-body text-sm mb-7 leading-relaxed">
                Book your free class today.
              </p>

              <LeadForm />
            </motion.div>

            {/* RIGHT — Contact info */}
            <div className="flex flex-col gap-8 pt-2 h-full">
              <motion.div {...fadeUp(0.15)}>
                <span className="inline-block text-xs font-display font-semibold tracking-widest uppercase text-gold mb-3">
                  Reach Us Directly
                </span>
                <h2 className="font-display font-bold text-2xl text-foreground mb-6">
                  Contact Information
                </h2>

                <ul className="space-y-5">
                  {[
                    {
                      icon: <Phone className="w-4 h-4 text-foreground" />,
                      label: "Phone",
                      content: (
                        <a
                          href="tel:+15416003371"
                          className="text-foreground font-body font-medium text-lg hover:text-gold transition-colors-fast"
                          data-ocid="contact-phone-link"
                        >
                          (541) 600-3371
                        </a>
                      ),
                    },
                    {
                      icon: <Mail className="w-4 h-4 text-foreground" />,
                      label: "Email",
                      content: (
                        <a
                          href="mailto:shjjoffice@gmail.com"
                          className="text-foreground font-body font-medium text-lg hover:text-gold transition-colors-fast break-all"
                          data-ocid="contact-email-link"
                        >
                          shjjoffice@gmail.com
                        </a>
                      ),
                    },
                    {
                      icon: <MapPin className="w-4 h-4 text-foreground" />,
                      label: "Address",
                      content: (
                        <address className="not-italic text-foreground font-body font-medium text-base leading-relaxed">
                          1485 Mohawk Blvd
                          <br />
                          Springfield, OR 97477
                        </address>
                      ),
                    },
                  ].map((item, i) => (
                    <motion.li
                      key={item.label}
                      {...fadeUp(0.25 + i * 0.1)}
                      className="flex items-start gap-4"
                    >
                      <span className="mt-0.5 w-10 h-10 flex-shrink-0 rounded-xl bg-gradient-accent flex items-center justify-center shadow-gold">
                        {item.icon}
                      </span>
                      <div>
                        <p className="font-display font-semibold text-sm text-muted-foreground uppercase tracking-wider mb-0.5">
                          {item.label}
                        </p>
                        {item.content}
                      </div>
                    </motion.li>
                  ))}
                </ul>
              </motion.div>

              {/* Service areas */}
              <motion.div
                {...fadeUp(0.55)}
                className="bg-muted/50 rounded-2xl p-6 border border-border"
              >
                <p className="font-display font-semibold text-sm text-muted-foreground uppercase tracking-wider mb-3">
                  Proudly Serving
                </p>
                <div className="flex flex-wrap gap-2 mb-3">
                  {serviceAreas.map((area) => (
                    <span
                      key={area}
                      className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-card border border-border text-sm font-body text-foreground"
                    >
                      <span className="w-1.5 h-1.5 rounded-full bg-gold flex-shrink-0" />
                      {area}
                    </span>
                  ))}
                </div>
                <p className="text-muted-foreground font-body text-sm leading-relaxed">
                  and surrounding Lane County areas.
                </p>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* Full-width Google Map section */}
      <section className="bg-background pb-14 px-4">
        <div className="container mx-auto max-w-5xl">
          <motion.div {...fadeUp(0)}>
            <span className="inline-block text-xs font-display font-semibold tracking-widest uppercase text-gold mb-3">
              Our Location
            </span>
            <h2 className="font-display font-bold text-2xl text-foreground mb-6">
              Find Us
            </h2>
          </motion.div>
          <motion.div
            {...fadeUp(0.1)}
            className="rounded-2xl overflow-hidden border border-border shadow-card"
            style={{ height: 420 }}
          >
            <iframe
              title="Superhero Jiu Jitsu Academy location map"
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2877.5!2d-123.012!3d44.046!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x54c119b2e4f57d6f%3A0x1!2s1485+Mohawk+Blvd%2C+Springfield%2C+OR+97477!5e0!3m2!1sen!2sus!4v1"
              width="100%"
              height="100%"
              style={{ border: 0 }}
              allowFullScreen
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
          </motion.div>
        </div>
      </section>

      {/* Footer override section removed — Layout renders the footer */}
    </Layout>
  );
}
